<template>
  <div>
    <modal-send-merge-request ref="modalSendMergeRequest" />
    <b-modal
      :title="t('diffModal.rebaseAlertTitle')"
      v-model="isRebaseAlertOpen"
      :ok-title="t('diffModal.rebaseAlertButton')"
      @ok="openModal('rebase')"
    >
      {{ t('diffModal.rebaseAlertMessage') }}
    </b-modal>
    <b-modal
      size="xl"
      v-model="isModalOpen"
      content-class="background-color-content"
      ok-title="Validate"
      :teleport-disabled="true"
      id="branch-change-modal"
    >
      <template #title>
        <div class="header">
          <b-placeholder v-if="loading" animation="glow" class="placeholder-modal-title"></b-placeholder>
          <div v-else>
            <span>{{ title }}</span>
            <span class="affected-elements">{{ `${checkboxValues.length} elements affected` }}</span>
          </div>
        </div>
      </template>
      <div class="modal-body">
        <b-tabs pills v-if="!loading">
          <template #tabs-end>
            <div class="Check-all-wrapper" v-if="isModalRebase">
              <b-form-checkbox
                @change="handleCheckboxAll"
                v-model="takeAllCheckboxValue"
                switch
                size="lg"
              ></b-form-checkbox>
              <label>{{ t('diffModal.TakeAllChanges') }}</label>
            </div>
          </template>
          <b-tab
            v-for="(tab, index) in filteredTabsData"
            :data-testid="tab.isTabWithAll ? 'all-tab' : null"
            :key="tab.title"
            :title="tab.title"
            :active="index === 0"
          >
            <div class="tab-content scrollbar-styling">
              <div v-for="(accordion, _index) in tab.accordions" :key="_index">
                <accordion-branch-change
                  :title="accordion.title"
                  :status-master="accordion.statuses.master"
                  :status-branch="accordion.statuses.branch"
                  :modal-type="modalType"
                  :fields="accordion.fields"
                  :id="accordion.id"
                  @update:model-value="status => handleCheckboxValue(accordion.id_accordion, status)"
                  :model-value="getCheckboxValue(accordion.id_accordion)"
                />
              </div>
            </div>
          </b-tab>
        </b-tabs>
        <b-tabs pills v-else>
          <b-tab title="All">
            <b-accordion disabled class="placeholder-accordion" v-for="index in [1, 2, 3, 4, 5, 6]" :key="index">
              <b-accordion-item>
                <template #title>
                  <b-placeholder animation="glow" class="placeholder-title me-2" />
                  <b-placeholder animation="glow" class="placeholder-title-sm" />
                </template>
              </b-accordion-item>
            </b-accordion>
          </b-tab>
        </b-tabs>
      </div>
      <template #footer>
        <template v-if="loading">
          <b-placeholder-button animation="glow" variant="secondary" class="placeholder-btn-sm" />
          <b-placeholder-button animation="glow" class="placeholder-btn" />
        </template>
        <template v-else-if="(isModalRebase || isModalMerge) && !loading">
          <span>{{
            t(isModalRebase ? 'diffModal.recapRebase' : 'diffModal.recapMerge', [
              isModalRebase ? numberOfSelectedMasterChanges : numberOfSelectedBranchChanges,
              checkboxValues.length,
            ])
          }}</span>
          <b-button @click="isModalOpen = false">
            {{ $t('common.cancel') }}
          </b-button>
          <b-button
            variant="primary"
            @click="rebaseBranch"
            v-if="isModalRebase"
            :disabled="mergeLoading"
            :loading="mergeLoading"
          >
            {{ $t('diffModal.addToBranch') }}
          </b-button>
          <b-button
            variant="primary"
            @click="mergeBranch"
            v-if="isModalMerge"
            :disabled="mergeLoading"
            :loading="mergeLoading"
          >
            {{ $t('diffModal.mergeBranch') }}
          </b-button>
        </template>
        <b-button v-else @click="isModalOpen = false">{{ $t('common.close') }}</b-button>
      </template>
    </b-modal>
  </div>
</template>
<script setup>
import accordionBranchChange from '@components/containers/accordion-branch-change';
import ModalSendMergeRequest from '@components/containers/modal-send-merge-request.vue';
import { axiosApi } from '@models/axios';
import {
  generateAccordionForTags,
  generateAccordionForEvents,
  generateAccordionForRules,
  generateAccordionForCustomJs,
  generateAccordionConfig,
} from '@common/containers/branch-change-orchestrator';
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const { t } = useI18n({ useScope: 'global' });
const store = useStore();
const isModalOpen = defineModel();
const emit = defineEmits(['update-rebase-number', 'reload']);
const router = useRouter();

const filteredTabsData = ref([]);
const checkboxValues = ref([]);
const takeAllCheckboxValue = ref(false);
const modalType = ref(null);
const loading = ref(false);
const mergeLoading = ref(false);
const isRebaseAlertOpen = ref(false);
const modalContainerID = ref(null);
const numberOfUnhandledChangesToRebase = ref(0);
const modalSendMergeRequest = ref(null);
const modalTypes = {
  REBASE_ALERT: 'rebaseAlert',
  REBASE: 'rebase',
  MERGE: 'merge',
  DIFF: 'diff',
  SEND_MERGE_REQUEST: 'sendMergeRequest',
};

onMounted(() => {
  // make the open modal available in the global scope for test to open it
  window.openModalBranch = openModal;
});

const title = computed(() => {
  if (isModalRebase.value) {
    return t('diffModal.title', [
      store.state.tms.webContainer.entity.name,
      store.state.tms.webContainer.entities?.find(
        container => store.state.tms.webContainer.entity.parent_id == container.id
      )?.name,
    ]);
  } else if (isModalDiff.value) {
    const branch = store.state.tms.webContainer.entities?.find(container => modalContainerID.value == container.id);
    return t('diffModal.titleDiff', [
      branch.name,
      store.state.tms.webContainer.entities?.find(container => branch.parent_id == container.id)?.name,
    ]);
  } else {
    return t('diffModal.titleMerge', [
      store.state.tms.webContainer.entity.name,
      store.state.tms.webContainer.entities?.find(
        container => store.state.tms.webContainer.entity.parent_id == container.id
      )?.name,
    ]);
  }
});

const numberOfSelectedMasterChanges = computed(() => {
  return checkboxValues.value.filter(element => element.status === 'master').length;
});

const numberOfSelectedBranchChanges = computed(() => {
  return checkboxValues.value.filter(element => element.status === 'branch').length;
});

const setNumberOfUnhandledChangesToRebase = data => {
  if (!data) {
    numberOfUnhandledChangesToRebase.value = 0;
    return emit('update-rebase-number', 0);
  }
  const tabs = getTabsDataForRebase(data);
  numberOfUnhandledChangesToRebase.value = tabs.find(tab => tab.title === 'All')?.accordions?.length || 0;
  emit('update-rebase-number', numberOfUnhandledChangesToRebase.value);
};

const getCheckboxValue = id => {
  const element = checkboxValues.value.find(element => element.id === id);
  return element ? element.status : 'branch';
};

const handleCheckboxValue = (id, status) => {
  const element = checkboxValues.value.find(element => element.id === id);
  element.status = status;
  if (element.status === 'branch') {
    takeAllCheckboxValue.value = false;
  }

  if (checkboxValues.value.reduce((acc, curr) => acc && curr.status === 'master', true)) {
    takeAllCheckboxValue.value = true;
    checkboxValues.value.map(element => {
      element.statusCopy = 'branch';
    });
  } else {
    takeAllCheckboxValue.value = false;
  }
};

const handleCheckboxAll = event => {
  const value = event.target.checked;
  if (value) {
    checkboxValues.value.forEach(element => {
      element.statusCopy = element.status;
      element.status = 'master';
    });
  } else {
    checkboxValues.value.forEach(element => {
      element.status = element.statusCopy || 'branch';
    });
  }
};

// Fetch the data for the modal
// fetchDataFromLastRebase: if true, fetch the data from the last rebase, return the complete list of changes ( used when displaying what's been done on the branch )
const fetchData = async (containerID, fetchDataFromLastRebase = true) => {
  modalContainerID.value = containerID;
  const allTabs = [];
  if (!store.state.tms.webContainer?.entities?.find(({ id }) => id == containerID)?.parent_id) return;
  try {
    const diffPayloadPomise = await axiosApi.get(`/tms/web-containers/${containerID}/differences-with-master`, {
      params: {
        'from-last-merge': fetchDataFromLastRebase,
      },
    });

    // Check if ruleCategory and triggerCategory data is already loaded
    // If not, dispatch the corresponding actions to fetch the data
    const ruleCategoryPromise =
      store.state.tms?.ruleCategory?.entities?.length === 0 ? store.dispatch('tms/ruleCategory/findAll') : null;

    const triggerCategoryPromise =
      store.state.tms?.triggerCategory?.entities?.length === 0 ? store.dispatch('tms/triggerCategory/findAll') : null;

    // Await all promises: the diffPayload and any necessary dispatches
    await Promise.all([
      diffPayloadPomise,
      ...(ruleCategoryPromise ? [ruleCategoryPromise] : []),
      ...(triggerCategoryPromise ? [triggerCategoryPromise] : []),
    ]);

    const data = diffPayloadPomise.data;

    allTabs.push(generateAccordionForTags(data, t));
    allTabs.push(generateAccordionForEvents(data, t));
    allTabs.push(generateAccordionForRules(data, t));
    allTabs.push(generateAccordionForCustomJs(data, t));
    allTabs.push(generateAccordionConfig(data, t));

    allTabs.unshift({
      title: 'All',
      isTabWithAll: true,
      accordions: allTabs.map(segment => segment.accordions).flat(),
    });
    return allTabs;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const isModalRebase = computed(() => modalType.value === modalTypes.REBASE);
const isModalMerge = computed(() => modalType.value === modalTypes.MERGE);
const isModalDiff = computed(() => modalType.value === modalTypes.DIFF);

const openModal = async (type = modalTypes.REBASE, containerID = store.state.tms.webContainer?.entity?.id) => {
  loading.value = true;

  switch (type) {
    case modalTypes.SEND_MERGE_REQUEST:
      const dataMergeRequest = await fetchData(containerID, true);
      const rebaseDataMergeRequest = getTabsDataForRebase(dataMergeRequest);

      if (rebaseDataMergeRequest?.length === 0) {
        modalSendMergeRequest.value.openModal();
        modalType.value = modalTypes.MERGE;
        break;
      }
      modalType.value = modalTypes.REBASE_ALERT;
    case modalTypes.REBASE_ALERT:
      isRebaseAlertOpen.value = true;
      return;
    case modalTypes.MERGE:
      isModalOpen.value = true;
      const data = await fetchData(containerID, true);
      const rebaseData = getTabsDataForRebase(data);
      if (rebaseData?.length === 0) {
        const dataMerge = await fetchData(containerID, false);
        filteredTabsData.value = getTabsDataForDiffAndMerge(dataMerge);
        modalType.value = modalTypes.MERGE;
      } else {
        modalType.value = modalTypes.REBASE_ALERT;
        isRebaseAlertOpen.value = true;
        isModalOpen.value = false;
      }
      break;
    case modalTypes.REBASE:
      isModalOpen.value = true;
      const dataRebase = await fetchData(containerID, true);
      filteredTabsData.value = getTabsDataForRebase(dataRebase);

      modalType.value = modalTypes.REBASE;
      break;

    case modalTypes.DIFF:
      isModalOpen.value = true;
      const dataDiff = await fetchData(containerID, false);
      filteredTabsData.value = getTabsDataForDiffAndMerge(dataDiff);
      modalType.value = modalTypes.DIFF;
      break;

    default:
      filteredTabsData.value = [];
      modalType.value = modalTypes.NONE;
      break;
  }

  // Update checkbox values after fetching data
  checkboxValues.value =
    filteredTabsData.value
      ?.find(tab => tab.isTabWithAll)
      ?.accordions.map(accordion => ({
        id: accordion.id_accordion,
        status: accordion?.statuses?.branch ? 'branch' : 'master',
      })) || [];

  if (checkboxValues.value.reduce((acc, curr) => acc && curr.status === 'master', true)) {
    takeAllCheckboxValue.value = true;
  }

  loading.value = false;
};

const filterTabsData = (unfilteredAccordionData, filterCondition) => {
  if (!unfilteredAccordionData) return [];
  return unfilteredAccordionData
    .map(tab => ({
      ...tab,
      accordions: tab.accordions.filter(filterCondition),
    }))
    .filter(tab => tab.accordions.some(accordion => accordion.fields.length > 0));
};

const getTabsDataForRebase = unfilteredAccordionData => {
  // only display changes that come from the master
  return filterTabsData(
    unfilteredAccordionData,
    accordion => Boolean(accordion?.statuses?.master) && accordion.fields.length > 0
  );
};

const getTabsDataForDiffAndMerge = unfilteredAccordionData => {
  // only display changes that come from the branch
  return filterTabsData(
    unfilteredAccordionData,
    accordion => Boolean(accordion?.statuses?.branch) && accordion.fields.length > 0
  );
};

const getPayload = (allTabs, isRebase) => {
  const payload = {};
  const allAccordions = allTabs.find(tab => tab.isTabWithAll)?.accordions;

  checkboxValues.value.forEach(element => {
    const accordion = allAccordions.find(accordion => accordion.id_accordion === element.id);
    const elementType = accordion?.payloadMerge?.elementType;
    if (!elementType) return;

    let mergePayloadType;

    if (isRebase) {
      if (element.status === 'master') {
        mergePayloadType = 'masterMerge';
      } else {
        mergePayloadType = 'masterRefuse';
      }
    } else {
      if (element.status === 'branch') {
        mergePayloadType = 'branchMerge';
      } else {
        mergePayloadType = 'branchRefuse';
      }
    }

    if (elementType === 'config') {
      payload[elementType] = [...(payload[elementType] || []), ...(accordion?.payloadMerge[mergePayloadType] || [])];
    } else {
      payload[elementType] = payload[elementType] || [];
      payload[elementType].push(accordion?.payloadMerge[mergePayloadType]);
    }
  });

  return payload;
};

const getRebasePayload = allTabs => getPayload(allTabs, true);
const getMergePayload = allTabs => getPayload(allTabs, false);

const rebaseBranch = async () => {
  const payload = getRebasePayload(filteredTabsData.value);
  const result = await callMerge(
    payload,
    {},
    store.state.tms.webContainer?.entity?.parent_id,
    store.state.tms.webContainer?.entity?.id
  );
  if (result) {
    store.commit('notifications/pushSuccess', {
      text: 'Branch updated',
      timeout: true,
    });
    emit('reload');
    setNumberOfUnhandledChangesToRebase(null);
    isModalOpen.value = false;
  }
};

const mergeBranch = async () => {
  const payload = getMergePayload(filteredTabsData.value);
  const result = await callMerge(
    payload,
    {
      params: {
        final_merge: true,
      },
    },
    store.state.tms.webContainer?.entity?.id,
    store.state.tms.webContainer?.entity?.parent_id
  );
  if (result) {
    store.commit('notifications/pushSuccess', {
      text: 'Branch merged',
      timeout: true,
    });
    emit('reload');
    setNumberOfUnhandledChangesToRebase(null);
    isModalOpen.value = false;
    router.push({
      name: 'tag-container-generate',
      params: { containerID: store.state.tms.webContainer?.entity?.parent_id },
    });
  }
};

const callMerge = async (payload, params, sourceId, targetId) => {
  mergeLoading.value = true;
  let result = null;
  try {
    result = await axiosApi.post(`/tms/web-containers/${targetId}/merge/${sourceId}`, payload, params);
  } catch (e) {
    console.error(e);
    store.commit('notifications/pushWarning', {
      text: `something went wrong: ${e}`,
      timeout: true,
    });
  }
  mergeLoading.value = false;
  return result;
};

watch(
  // refretch the data so that the display state of the rebase alert reflect the current web container
  () => store.state.tms.webContainer?.entity?.id,
  async (newVal, oldVal) => {
    if (newVal !== oldVal) {
      const data = await fetchData(newVal);
      setNumberOfUnhandledChangesToRebase(data);
    }
  },
  { immediate: true }
);

defineExpose({
  openModal,
  numberOfUnhandledChangesToRebase,
});
</script>
<style lang="scss" scoped>
@import '@styles/variables.scss';

.tab-content {
  margin-top: 1em;
  height: 67vh;
  overflow-y: auto;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1em;
}

.header {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.Check-all-wrapper {
  display: flex;
  align-items: center;
  margin-left: 1em;
  margin-top: -2px;

  > label {
    margin-bottom: 0;
  }

  :first-child {
    margin-right: -1em;
  }
}

.affected-elements {
  font-size: medium;
  font-weight: 400;
  margin-left: 1em;
}

:deep(.placeholder-accordion) {
  margin-top: 1em;
}

:deep(.placeholder-title) {
  width: 250px;
  height: 1em;
}
:deep(.placeholder-title-sm) {
  width: 50px;
  height: 1em;
}

:deep(.placeholder-modal-title) {
  width: 350px;
  height: 1em;
}

:deep(.placeholder-btn-sm) {
  width: 72px;
}
:deep(.placeholder-btn) {
  width: 119px;
}

// make the modal larger
:deep(#branch-change-modal) .modal-dialog {
  --bs-modal-width: calc(80vw - 3.5em);
}

:deep(.modal-title) {
  width: 100%;
}

.alert-container {
  width: 100%;
  height: 0px;
  position: relative;
  display: flex;
  padding-left: 2em;
  justify-content: flex-start;
  > .alert {
    position: absolute;
    top: 73px;
    display: flex;
    align-items: center;
    gap: 1em;
    padding: 0.5em;
    justify-content: center;
  }
}
</style>
